import { Divider } from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import {
  useAdminChats,
  type AdminChatsFilters,
} from "@/shared/domain/admin/hooks/useAdminChats";

import { AdminChatViewer } from "./AdminChatViewer";
import {
  ChatFilters,
  type ChatFiltersValues,
  type FilterOption,
} from "./ChatFilters";
import { ChatsTable } from "./ChatsTable";
import styles from "./ExploreChats.module.css";

const PAGE_SIZE = 20;

const EMPTY_FILTERS: ChatFiltersValues = {
  org: null,
  user: null,
  sessionId: "",
};

export function ExploreChats() {
  const { orgService } = useDomainServices();

  // Fetch all orgs for autocomplete
  const { data: allOrgs = [], isLoading: orgsLoading } = useQuery({
    queryKey: ["allOrgs"],
    queryFn: ({ signal }) => orgService.getAllOrgs({ abortSignal: signal }),
    staleTime: 60_000,
  });

  // Fetch users for each org
  const orgUserQueries = useQueries({
    queries: allOrgs.map((org) => ({
      queryKey: ["orgUsers", org.org_id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        orgService.getOrgUsers(org.org_id, { abortSignal: signal }),
      staleTime: 60_000,
      enabled: allOrgs.length > 0,
    })),
  });

  const usersLoading = orgUserQueries.some((q) => q.isLoading);

  // Build org options: "OrgName (org_id)"
  const orgOptions: FilterOption[] = React.useMemo(
    () =>
      allOrgs.map((o) => ({
        label: `${o.name} (${o.org_id})`,
        id: o.org_id,
      })),
    [allOrgs],
  );

  // Build user options: "DisplayName (user_id)" — deduplicated across orgs
  const userOptions: FilterOption[] = React.useMemo(() => {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    for (const query of orgUserQueries) {
      for (const orgUser of query.data ?? []) {
        if (!seen.has(orgUser.user.user_id)) {
          seen.add(orgUser.user.user_id);
          const displayName = orgUser.user.name
            ? `${orgUser.user.name} (${orgUser.user.user_id})`
            : orgUser.user.user_id;
          options.push({
            label: displayName,
            id: orgUser.user.user_id,
          });
        }
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [orgUserQueries]);

  const [filterValues, setFilterValues] =
    React.useState<ChatFiltersValues>(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = React.useState<AdminChatsFilters>(
    {},
  );
  const [selectedChatId, setSelectedChatId] = React.useState<
    string | undefined
  >();
  const [pageToken, setPageToken] = React.useState<string | undefined>();
  const [pageTokenHistory, setPageTokenHistory] = React.useState<string[]>([]);
  const [currentPage, setCurrentPage] = React.useState(0);

  const queryFilters: AdminChatsFilters = React.useMemo(
    () => ({
      ...activeFilters,
      pageToken,
      limit: PAGE_SIZE,
    }),
    [activeFilters, pageToken],
  );

  const { data, isLoading } = useAdminChats(queryFilters);

  const handleSearch = () => {
    setSelectedChatId(undefined);
    setPageToken(undefined);
    setPageTokenHistory([]);
    setCurrentPage(0);
    setActiveFilters({
      orgId: filterValues.org?.id,
      createdBy: filterValues.user?.id,
      sessionId: filterValues.sessionId || undefined,
    });
  };

  const handleClear = () => {
    setFilterValues(EMPTY_FILTERS);
    setSelectedChatId(undefined);
    setPageToken(undefined);
    setPageTokenHistory([]);
    setCurrentPage(0);
    setActiveFilters({});
  };

  const handleNextPage = () => {
    if (data?.next_token) {
      setPageTokenHistory((prev) => [...prev, pageToken ?? ""]);
      setPageToken(data.next_token);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0 && pageTokenHistory.length > 0) {
      const prevToken = pageTokenHistory[pageTokenHistory.length - 1];
      setPageTokenHistory((prev) => prev.slice(0, -1));
      // Empty string means first page (no token needed)
      setPageToken(prevToken || undefined);
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleSelectChat = (chatId: string) => {
    setSelectedChatId(chatId);
  };

  const handleBack = () => {
    setSelectedChatId(undefined);
  };

  const chats = data?.items ?? [];

  return (
    <div className={styles.container}>
      <ChatFilters
        values={filterValues}
        onChange={setFilterValues}
        onSearch={handleSearch}
        onClear={handleClear}
        isLoading={isLoading}
        orgOptions={orgOptions}
        userOptions={userOptions}
        orgOptionsLoading={orgsLoading}
        userOptionsLoading={usersLoading}
      />

      <Divider />

      <ChatsTable
        chats={chats}
        isLoading={isLoading}
        selectedChatId={selectedChatId}
        onSelectChat={handleSelectChat}
        hasNextPage={!!data?.next_token}
        hasPreviousPage={currentPage > 0}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        currentPage={currentPage}
      />

      {selectedChatId && (
        <>
          <Divider />
          <AdminChatViewer chatId={selectedChatId} onBack={handleBack} />
        </>
      )}
    </div>
  );
}
